/**
 * 动态表单渲染器
 * 根据 form_components 配置动态渲染表单字段
 */

import type { ReactElement } from 'react'
import { motion } from 'framer-motion'
import {
  type FormComponent,
  type FormState,
  type FormErrors,
  type ComponentType,
  type ComponentCategory,
  COMPONENT_TYPES,
  COMPONENT_CATEGORIES,
  COMPONENT_CATEGORY_MAP,
  groupComponentsByCategory,
} from '@/types/workflow'
import {
  PositivePromptField,
  NegativePromptField,
  SingleSelectChipsField,
  NumberSliderField,
  CustomNumberField,
  ControlSwitchField,
  SingleImageUploadField,
  MultiImageUploadField,
} from './form-fields'
import type { FormFieldComponent } from './form-fields'

/**
 * 组件类型到渲染器的映射
 */
const FIELD_COMPONENTS: Partial<Record<ComponentType, FormFieldComponent>> = {
  [COMPONENT_TYPES.POSITIVE_PROMPT]: PositivePromptField,
  [COMPONENT_TYPES.NEGATIVE_PROMPT]: NegativePromptField,
  [COMPONENT_TYPES.SINGLE_SELECT_CHIPS]: SingleSelectChipsField,
  [COMPONENT_TYPES.NUMBER_SLIDER]: NumberSliderField,
  [COMPONENT_TYPES.CUSTOM_NUMBER]: CustomNumberField,
  [COMPONENT_TYPES.CONTROL_SWITCH]: ControlSwitchField,
  [COMPONENT_TYPES.SINGLE_IMAGE_UPLOAD]: SingleImageUploadField,
  [COMPONENT_TYPES.MULTI_IMAGE_UPLOAD]: MultiImageUploadField,
  // 扩展组件（使用已有组件复用）
  [COMPONENT_TYPES.REDRAW_STRENGTH]: NumberSliderField,
  [COMPONENT_TYPES.RATIO_SELECT]: SingleSelectChipsField,
  [COMPONENT_TYPES.CUSTOM_SELECT_TEXT]: SingleSelectChipsField,
  [COMPONENT_TYPES.CUSTOM_SELECT_NUMBER]: SingleSelectChipsField,
}

/**
 * 分类渲染顺序（按填写重要性排序）
 */
const CATEGORY_ORDER: ComponentCategory[] = [
  COMPONENT_CATEGORIES.IMAGE,
  COMPONENT_CATEGORIES.PROMPT,
  COMPONENT_CATEGORIES.SELECT,
  COMPONENT_CATEGORIES.BASE,
  COMPONENT_CATEGORIES.COLOR_SELECT,
]

/**
 * 动态表单渲染器属性
 */
interface DynamicFormRendererProps {
  /** 表单组件配置列表 */
  components: FormComponent[]
  /** 表单状态 */
  formState: FormState
  /** 表单错误 */
  errors: FormErrors
  /** 字段值变化回调 */
  onChange: (paramName: string, value: unknown) => void
  /** 是否禁用 */
  disabled?: boolean
  /** 是否显示分组标题 */
  showGroupTitles?: boolean
  /** 用户当前算力（用于共享算力场景的上传限制，undefined 表示不启用算力限制） */
  userCredits?: number | undefined
  /** 单次消耗算力（用于计算上传限制） */
  basePower?: number
  /** 是否启用多组提示词 */
  enableMultiPrompt?: boolean
  /** 是否限制共享用户选择高倍率配置 */
  restrictHighCostOptions?: boolean
  /** 受限选项提示文案 */
  restrictedOptionMessage?: string
}

/**
 * 容器动画配置
 */
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
}

/**
 * 项目动画配置
 */
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
    },
  },
}

/**
 * 生成表单组件渲染 key。
 * @param component 表单组件配置
 * @returns 稳定且唯一的 React key
 */
function getComponentRenderKey(component: FormComponent): string {
  return `${component.id}:${component.param_name}:${String(component.sort_order)}`
}

/**
 * 动态表单渲染器
 * @param props 组件属性
 * @returns 组件元素
 */
export function DynamicFormRenderer({
  components,
  formState,
  errors,
  onChange,
  disabled = false,
  showGroupTitles = true,
  userCredits,
  basePower,
  enableMultiPrompt,
  restrictHighCostOptions = false,
  restrictedOptionMessage,
}: DynamicFormRendererProps): ReactElement {
  // 按分类分组
  const groupedComponents = groupComponentsByCategory(components)

  /**
   * 创建字段变化处理器
   * @param paramName 参数名
   * @returns 变化处理函数
   */
  function createChangeHandler(paramName: string): (value: unknown) => void {
    return (value: unknown): void => {
      onChange(paramName, value)
    }
  }

  /**
   * 渲染单个组件
   * @param component 组件配置
   * @returns 组件元素
   */
  function renderComponent(component: FormComponent): ReactElement | null {
    const FieldComponent = FIELD_COMPONENTS[component.component_type]

    if (FieldComponent === undefined) {
      // 未实现的组件类型，跳过渲染
      console.warn(`未实现的组件类型: ${component.component_type}`)
      return null
    }

    return (
      <motion.div key={getComponentRenderKey(component)} variants={itemVariants}>
        <FieldComponent
          component={component}
          value={formState[component.param_name]}
          onChange={createChangeHandler(component.param_name)}
          error={errors[component.param_name]}
          disabled={disabled}
          {...(userCredits !== undefined && { userCredits })}
          {...(basePower !== undefined && { basePower })}
          {...(enableMultiPrompt !== undefined && { enableMultiPrompt })}
          restrictHighCostOptions={restrictHighCostOptions}
          {...(restrictedOptionMessage !== undefined && { restrictedOptionMessage })}
        />
      </motion.div>
    )
  }

  /**
   * 渲染分类组
   * @param category 分类
   * @param categoryComponents 该分类下的组件
   * @returns 组件元素
   */
  function renderCategoryGroup(
    category: ComponentCategory,
    categoryComponents: FormComponent[]
  ): ReactElement {
    const categoryName = COMPONENT_CATEGORY_MAP[category]

    return (
      <div key={category} className="space-y-4">
        {showGroupTitles && (
          <h3 className="text-sm font-medium text-muted-foreground">
            {categoryName}
          </h3>
        )}
        <div className="space-y-4">
          {categoryComponents.map((component) => renderComponent(component))}
        </div>
      </div>
    )
  }

  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {CATEGORY_ORDER.map((category) => {
        const categoryComponents = groupedComponents[category]
        if (categoryComponents === undefined || categoryComponents.length === 0) {
          return null
        }
        return renderCategoryGroup(category, categoryComponents)
      })}
    </motion.div>
  )
}
